/**
 * TypeScript wrapper for WASM proportional-editing weight computation.
 *
 * Mirrors the signature of the original `modeling/proportional.ts` so
 * callers don't change. Internally packs the selected-vertex `Set` into
 * a `Uint32Array`, invokes WASM, and reconstructs the
 * `Map<number, number>` shape consumed by the command-buffer readers in
 * `transformMath.ts` and `commandApplyLiveEdit.ts`.
 */

import { compute_proportional_weights as wasmComputeProportionalWeights } from "./pkg/poseref_wasm.js";

export type ProportionalFalloff = "smooth" | "linear" | "sharp" | "constant";

const FALLOFF_CODE: Record<ProportionalFalloff, number> = {
  smooth: 0,
  linear: 1,
  sharp: 2,
  constant: 3,
};

export function computeProportionalWeights(
  vertices: Float32Array,
  selectedVertices: ReadonlySet<number>,
  radius: number,
  falloff: ProportionalFalloff,
): Map<number, number> {
  const weights = new Map<number, number>();
  if (radius <= 0 || selectedVertices.size === 0) return weights;

  const selected = new Uint32Array(selectedVertices.size);
  let i = 0;
  for (const vi of selectedVertices) selected[i++] = vi;

  const result = wasmComputeProportionalWeights(
    vertices,
    selected,
    radius,
    FALLOFF_CODE[falloff],
  );
  const indices = result.indices;
  const weightBuf = result.weights;
  result.free();

  for (let k = 0; k < indices.length; k++) {
    weights.set(indices[k], weightBuf[k]);
  }
  return weights;
}
